const DB_NAME = 'reelpro-studio';
const DB_VERSION = 4;
const PROJECT_KEY = 'active-project';
const PROFILE_BACKUP_KEY = 'reelpro-profile-library-v1';

function readProfileBackup() {
  try {
    const value = JSON.parse(localStorage.getItem(PROFILE_BACKUP_KEY) || '[]');
    return Array.isArray(value) ? value : [];
  } catch { return []; }
}

function writeProfileBackup(profiles) {
  try { localStorage.setItem(PROFILE_BACKUP_KEY, JSON.stringify(profiles || [])); } catch {}
}

function openDb() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains('projects')) db.createObjectStore('projects');
      if (!db.objectStoreNames.contains('profiles')) db.createObjectStore('profiles', { keyPath: 'id' });
      if (!db.objectStoreNames.contains('fonts')) db.createObjectStore('fonts', { keyPath: 'id' });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function transact(mode, callback) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('projects', mode);
    const store = tx.objectStore('projects');
    const result = callback(store);
    tx.oncomplete = () => { db.close(); resolve(result?.result); };
    tx.onerror = () => { db.close(); reject(tx.error); };
  });
}

async function transactStore(storeName, mode, callback) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const request = callback(store);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

export async function saveProject(project) {
  const portableMedia = (item) => {
    const { url, ...stored } = item;
    return stored;
  };
  const snapshot = {
    version: 2,
    savedAt: Date.now(),
    script: project.script,
    scriptText: project.scriptText,
    trialScript: project.trialScript,
    trialScriptText: project.trialScriptText,
    settings: project.settings,
    activeProfileId: project.activeProfileId || '',
    images: project.images.map(portableMedia),
    trialImages: project.trialImages.map(portableMedia)
  };
  await transact('readwrite', (store) => store.put(snapshot, PROJECT_KEY));
}

export async function listProfiles() {
  const stored = (await transactStore('profiles', 'readonly', (store) => store.getAll())) || [];
  if (stored.length) { writeProfileBackup(stored); return stored; }
  const backup = readProfileBackup();
  for (const profile of backup) await transactStore('profiles', 'readwrite', (store) => store.put(profile));
  return backup;
}

export async function saveProfile(profile) {
  const value = { ...profile, updatedAt: Date.now() };
  await transactStore('profiles', 'readwrite', (store) => store.put(value));
  const profiles = readProfileBackup().filter(item => item.id !== value.id);
  profiles.push(value);
  writeProfileBackup(profiles);
  return value;
}

export async function deleteProfile(id) {
  await transactStore('profiles', 'readwrite', (store) => store.delete(id));
  writeProfileBackup(readProfileBackup().filter(item => item.id !== id));
}

export async function replaceProfiles(profiles) {
  const values = Array.isArray(profiles) ? profiles : [];
  const db = await openDb();
  await new Promise((resolve, reject) => {
    const tx = db.transaction('profiles', 'readwrite');
    const store = tx.objectStore('profiles');
    store.clear();
    for (const profile of values) store.put(profile);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error || new Error('Could not update profile cache.')); };
  });
  writeProfileBackup(values);
}

export async function listCustomFonts() {
  return (await transactStore('fonts', 'readonly', (store) => store.getAll())) || [];
}

export async function saveCustomFont(font) {
  await transactStore('fonts', 'readwrite', (store) => store.put(font));
  return font;
}

export async function deleteCustomFont(id) {
  await transactStore('fonts', 'readwrite', (store) => store.delete(id));
}

export async function loadProject() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('projects', 'readonly');
    const request = tx.objectStore('projects').get(PROJECT_KEY);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
    tx.oncomplete = () => db.close();
  });
}

export async function clearProject() {
  await transact('readwrite', (store) => store.delete(PROJECT_KEY));
}

export async function requestPersistentStorage() {
  if (!navigator.storage?.persist) return false;
  return navigator.storage.persist();
}

export async function getStorageEstimate() {
  if (!navigator.storage?.estimate) return null;
  return navigator.storage.estimate();
}
